import { useState, useRef, useCallback } from 'react';
import { axiosPost } from '../../../../../utils/axiosPost';
import { axiosPut } from '../../../../../utils/axiosPut';
import { devError } from '../../../../../utils/devLog';

const useChairReportActions = ({
  committeeId,
  committeeName,
  formData,
  reportId,
  setReportId,
  reportStatus,
  setReportStatus,
  isAdmin,
  isComplete,
  setIsDirty,
  setValidationErrors,
  setWarningErrors,
  validateFilledFields,
  validateAllFields,
  getIncompleteFields,
  showNotification,
  onReportSaved,
}) => {
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);

  const validationSummaryRef = useRef(null);

  const scrollToValidationSummary = () => {
    validationSummaryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const buildPayload = useCallback(
    (status) => ({
      data: {
        sectionalCommitteeId: committeeId,
        sectionalCommitteeName: committeeName,
        status,
        ...(status === 'submitted' && {
          submittedAt: new Date().toISOString(),
        }),
        gapAnalysis: formData.gapAnalysis,
        evaluationProcess: formData.evaluationProcess,
        integrityMatters: formData.integrityMatters,
        diversityIssues: formData.diversityIssues,
        fnsFeedback: formData.fnsFeedback,
        generalObservations: formData.generalObservations,
        memberAttendance: formData.memberAttendance,
        candidateReports: formData.candidateReports,
      },
    }),
    [formData, committeeId, committeeName],
  );

  const handleSaveDraft = useCallback(async () => {
    const errors = validateFilledFields();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setWarningErrors([]);
      scrollToValidationSummary();
      showNotification(
        'Validation Error',
        'Please fix the highlighted fields before saving.',
        'fa-exclamation-triangle',
        'text-danger',
      );
      return false;
    }

    try {
      setSaving(true);
      setValidationErrors([]);
      const adminStatus =
        reportStatus === 'submitted' && !isComplete ? 'draft' : reportStatus;
      const payload = buildPayload(isAdmin ? adminStatus : 'draft');

      if (reportId) {
        await axiosPut(`chair-reports/${reportId}`, payload);
      } else {
        const response = await axiosPost('chair-reports', payload);
        setReportId(response?.data?.data?.id);
      }

      if (isAdmin && adminStatus !== reportStatus) {
        setReportStatus(adminStatus);
      }

      setIsDirty(false);
      onReportSaved?.();

      const incomplete = getIncompleteFields();
      const wordLimitErrors = validateFilledFields();
      setWarningErrors(incomplete);

      const viewDetailsAction = {
        label: 'View details',
        onClick: scrollToValidationSummary,
      };

      if (wordLimitErrors.length > 0) {
        setValidationErrors(wordLimitErrors);
        showNotification(
          'Draft Saved',
          'Your draft has been saved, but some fields exceed the word limit.',
          'fa-check',
          'text-warning',
          viewDetailsAction,
        );
      } else if (incomplete.length > 0) {
        showNotification(
          'Draft Saved',
          `Your draft has been saved. ${incomplete.length} field${incomplete.length === 1 ? '' : 's'} still need${incomplete.length === 1 ? 's' : ''} to be completed before submitting.`,
          'fa-check',
          'text-success',
          viewDetailsAction,
        );
      } else {
        showNotification(
          'Draft Saved',
          'Your report draft has been saved successfully. All fields are complete — ready to submit!',
          'fa-check',
          'text-success',
        );
      }
      return true;
    } catch (err) {
      devError('Error saving chair report:', err);
      showNotification(
        'Error',
        'Failed to save report. Please try again.',
        'fa-exclamation-triangle',
        'text-danger',
      );
      return false;
    } finally {
      setSaving(false);
    }
  }, [
    buildPayload,
    reportId,
    reportStatus,
    isAdmin,
    isComplete,
    validateFilledFields,
    getIncompleteFields,
    setValidationErrors,
    setWarningErrors,
    setIsDirty,
    setReportId,
    showNotification,
    onReportSaved,
  ]);

  const handleSubmit = useCallback(() => {
    const errors = validateAllFields();
    if (errors.length > 0) {
      setValidationErrors(errors);
      setWarningErrors([]);
      scrollToValidationSummary();
      showNotification(
        'Validation Error',
        'All fields must be completed before submitting.',
        'fa-exclamation-triangle',
        'text-danger',
      );
      return;
    }

    setShowSubmitModal(true);
  }, [
    validateAllFields,
    setValidationErrors,
    setWarningErrors,
    showNotification,
  ]);

  const handleConfirmedSubmit = useCallback(async () => {
    setShowSubmitModal(false);

    try {
      setSubmitting(true);
      setValidationErrors([]);
      setWarningErrors([]);
      const payload = buildPayload('submitted');

      if (reportId) {
        await axiosPut(`chair-reports/${reportId}`, payload);
      } else {
        const response = await axiosPost('chair-reports', payload);
        setReportId(response?.data?.data?.id);
      }

      setReportStatus('submitted');

      showNotification(
        'Report Submitted',
        'Your report has been submitted to Council successfully.',
        'fa-check',
        'text-success',
      );

      setIsDirty(false);
      onReportSaved?.();
    } catch (err) {
      devError('Error submitting chair report:', err);
      showNotification(
        'Error',
        'Failed to submit report. Please try again.',
        'fa-exclamation-triangle',
        'text-danger',
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    buildPayload,
    reportId,
    setValidationErrors,
    setWarningErrors,
    setIsDirty,
    setReportId,
    setReportStatus,
    showNotification,
    onReportSaved,
  ]);

  return {
    saving,
    submitting,
    showSubmitModal,
    setShowSubmitModal,
    validationSummaryRef,
    handleSaveDraft,
    handleSubmit,
    handleConfirmedSubmit,
  };
};

export default useChairReportActions;
